import { agentTaskOutputStatusSchema, llmOptional, proposalCommercialTermsSchema, proposalCriterionAssessmentSchema, proposalRiskScoreSchema, proposalSummarySchema, proposalVersionStatusSchema, rfpMatchSchema, rfpProposalReviewSnapshotSchema, signedContractAttachmentSchema, } from '@teo/types';
import { z } from 'zod';
export const agentTaskOutputHandleSchema = z.object({
    jobId: z.string(),
    agentTaskOutputDisplayId: z.string(),
});
export const agentTaskOutputDisplayIdInputSchema = z.object({
    agentTaskOutputDisplayId: z.string(),
});
export const getVendorNameOutputSchema = agentTaskOutputDisplayIdInputSchema;
export const vendorNameAgentResultSchema = z.object({
    vendorName: z.string().nullable(),
    evidenceText: llmOptional(z.string()),
    rfpMatch: llmOptional(rfpMatchSchema),
});
export const getVendorNameOutputResponseSchema = z.object({
    displayId: z.string(),
    status: agentTaskOutputStatusSchema,
    result: vendorNameAgentResultSchema.nullable(),
    failureReason: z.string().nullable(),
});
export const getDownloadUrlSchema = z.object({
    versionDisplayId: z.string(),
});
export const getDownloadUrlOutputSchema = z.object({
    signedUrl: z.string(),
    filename: z.string(),
    expiresAt: z.date(),
});
const completedAnalysisDataSchema = z.object({
    completedAt: z.string(),
    summary: proposalSummarySchema,
    commercialTerms: proposalCommercialTermsSchema,
    criterionAssessments: z.array(proposalCriterionAssessmentSchema),
    riskScores: z.array(proposalRiskScoreSchema),
});
const pendingAnalysisShape = {
    status: z.literal('pending'),
    stateData: z.record(z.string(), z.never()),
};
const completedAnalysisShape = {
    status: z.literal('completed'),
    stateData: completedAnalysisDataSchema,
};
const failedAnalysisShape = {
    status: z.literal('failed'),
    stateData: z.object({ failedAt: z.string(), failureReason: z.string() }),
};
export const latestAnalysisSchema = z.discriminatedUnion('status', [
    z.object({ displayId: z.string(), ...pendingAnalysisShape }),
    z.object({ displayId: z.string(), ...completedAnalysisShape }),
    z.object({ displayId: z.string(), ...failedAnalysisShape }),
]);
export const listThreadsForRfpOutputSchema = z.array(z.object({
    displayId: z.string(),
    vendorName: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
}));
export const getProposalVersionOutputSchema = z.object({
    version: z.object({
        displayId: z.string(),
        versionNumber: z.number(),
        uploadedAt: z.date(),
        createdAt: z.date(),
        updatedAt: z.date(),
    }),
    status: proposalVersionStatusSchema,
});
const analysisRunRowSchema = z.object({
    displayId: z.string(),
    startedAt: z.date(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const getAnalysisDetailOutputSchema = z.discriminatedUnion('status', [
    z.object({ run: analysisRunRowSchema, ...pendingAnalysisShape }),
    z.object({ run: analysisRunRowSchema, ...completedAnalysisShape }),
    z.object({ run: analysisRunRowSchema, ...failedAnalysisShape }),
]);
const versionVendorShape = {
    versionDisplayId: z.string(),
    versionNumber: z.number(),
    vendorName: z.string(),
};
export const getComparisonSessionOutputSchema = z.object({
    session: z.object({
        displayId: z.string(),
        rfpDisplayId: z.string(),
        createdAt: z.date(),
        updatedAt: z.date(),
    }),
    versions: z.array(z.object({ ...versionVendorShape, analysis: completedAnalysisDataSchema.optional() })),
});
export const listVersionsForRfpOutputSchema = z.array(z.object({ ...versionVendorShape, hasCompletedAnalysis: z.boolean() }));
export const getProposalThreadOutputSchema = z.object({
    proposalDisplayId: z.string(),
    rfpDisplayId: z.string(),
    vendorName: z.string(),
    versions: z.array(z.object({
        displayId: z.string(),
        versionNumber: z.number(),
        documentFilename: z.string().optional(),
        latestStatus: proposalVersionStatusSchema,
        statusHistory: z.array(z.object({ status: proposalVersionStatusSchema, createdAt: z.date() })),
        analysisRun: latestAnalysisSchema.optional(),
    })),
});
export const listComparisonSessionsForRfpOutputSchema = z.array(z.object({
    displayId: z.string(),
    createdAt: z.date(),
    versionCount: z.number(),
}));
const reviewIdentityShape = {
    snapshot: rfpProposalReviewSnapshotSchema,
};
const reviewerShape = {
    reviewNotes: z.string().optional(),
};
const reviewRowSchema = z.object({
    displayId: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const getActiveReviewForRfpOutputSchema = z
    .discriminatedUnion('status', [
    z.object({
        review: reviewRowSchema,
        status: z.literal('pending_review'),
        stateData: z.object(reviewIdentityShape),
    }),
    z.object({
        review: reviewRowSchema,
        status: z.literal('under_review'),
        stateData: z.object({
            ...reviewIdentityShape,
            ...reviewerShape,
            reviewStartedAt: z.string(),
        }),
    }),
    z.object({
        review: reviewRowSchema,
        status: z.literal('approved'),
        stateData: z.object({
            ...reviewIdentityShape,
            ...reviewerShape,
            approvedAt: z.string(),
            signedContract: signedContractAttachmentSchema.optional(),
        }),
    }),
    z.object({
        review: reviewRowSchema,
        status: z.literal('rejected'),
        stateData: z.object({ ...reviewIdentityShape, ...reviewerShape, rejectedAt: z.string() }),
    }),
    z.object({
        review: reviewRowSchema,
        status: z.literal('executed'),
        stateData: z.object({
            ...reviewIdentityShape,
            ...reviewerShape,
            approvedAt: z.string(),
            executedAt: z.string(),
            signedContract: signedContractAttachmentSchema,
        }),
    }),
])
    .nullable();
//# sourceMappingURL=proposals.js.map